import { useRef } from 'react';
import { Trans } from 'react-i18next';
import { t } from 'i18next';
import { Field, Form, Formik, FormikProps } from 'formik';

import { Box, Typography } from '@mui/material';

import {
  Button,
  ColumnProps,
  ConfirmDialog,
  FileUpload,
  Page,
  Table,
  useDialogState,
  useFilterConfig,
  usePanelState,
  useTableQueryParams,
} from 'components';
import { FilterOptionModel, ConsumptionPlaceUploadModel } from 'models';
import {
  useConsumptionPlaceUploadList,
  useUploadConsumptionPlaces,
  useDownloadConsumptionPlaceLog,
} from 'features/consumptionPlaceUpload';
import { getPageName } from 'utils/format';
import { RouteNames } from 'config/routeNames';
import { DATE_TIME_FORMAT, formatDate } from 'utils/dates';
import LeoTextField from 'components/input-fields/leoTextField/LeoTextField';

const STATUS_LABELS: Record<string, string> = {
  WAITING: 'FOGYASZTASI_HELYEK_BETOLTESE.STATUS_WAITING',
  PROCESSING: 'FOGYASZTASI_HELYEK_BETOLTESE.STATUS_PROCESSING',
  SUCCESS: 'FOGYASZTASI_HELYEK_BETOLTESE.STATUS_COMPLETED_SUCCESS',
  SUCCESS_WITH_ERRORS: 'FOGYASZTASI_HELYEK_BETOLTESE.STATUS_COMPLETED_WITH_ERRORS',
  INVALID_FORMAT: 'FOGYASZTASI_HELYEK_BETOLTESE.STATUS_INVALID_FORMAT',
  FAILED: 'FOGYASZTASI_HELYEK_BETOLTESE.STATUS_FAILED',
};

const styles = {
  logDownLoadButton: {
    textTransform: 'none',
  },
};

const ConsumptionPlaceUploadPage = () => {
  const formikRef = useRef<FormikProps<{ file: File[] | null }> | null>(null);

  const {
    onPageChange,
    onRowsPerPageChange,
    onFilter,
    onLoadQuery,
    onSort,
    params,
    onTableColumnVisibilityChange,
    apiParams,
  } = useTableQueryParams();

  const {
    data: listData,
    isLoading,
    isFetching,
    dataUpdatedAt,
  } = useConsumptionPlaceUploadList(apiParams);

  const { mutateAsync: uploadFiles, isPending: isUploading } = useUploadConsumptionPlaces();
  const { mutateAsync: downloadLog } = useDownloadConsumptionPlaceLog();

  const {
    isOpen: isConfirmOpen,
    openDialog: openConfirm,
    closeDialog: closeConfirm,
    selected: selectedFiles,
  } = useDialogState<File[]>();

  const filters: FilterOptionModel[] = [
    {
      headerTitle: 'FOGYASZTASI_HELYEK_BETOLTESE.SETTLEMENT',
      component: (
        <LeoTextField
          name="city"
          label={<Trans i18nKey="FOGYASZTASI_HELYEK_BETOLTESE.SETTLEMENT" />}
        />
      ),
      name: 'city',
      panelFieldWidth: 12,
      abbrName: t('FOGYASZTASI_HELYEK_BETOLTESE.SETTLEMENT'),
    },
    {
      headerTitle: 'FOGYASZTASI_HELYEK_BETOLTESE.JOB_ID',
      component: (
        <LeoTextField name="id" label={<Trans i18nKey="FOGYASZTASI_HELYEK_BETOLTESE.JOB_ID" />} />
      ),
      name: 'id',
      panelFieldWidth: 12,
      abbrName: t('FOGYASZTASI_HELYEK_BETOLTESE.JOB_ID'),
    },
  ];

  const filterConfig = useFilterConfig({
    params,
    onFilter,
    filters,
    onLoad: onLoadQuery,
    isButton: true,
    pageName: getPageName(RouteNames.KOZOS_FOGYASZTASI_HELYEK_BETOLTESE),
    isFetching,
  });

  const columns: ColumnProps<ConsumptionPlaceUploadModel>[] = [
    {
      key: 'city',
      header: <Trans i18nKey="FOGYASZTASI_HELYEK_BETOLTESE.SETTLEMENT" />,
      accessor: (item) => item.city || '-',
      sortable: true,
    },
    {
      key: 'id',
      header: <Trans i18nKey="FOGYASZTASI_HELYEK_BETOLTESE.JOB_ID" />,
      accessor: (item) => item.id || '-',
      field: 'id',
      sortable: true,
    },
    {
      key: 'originalFileName',
      header: <Trans i18nKey="FOGYASZTASI_HELYEK_BETOLTESE.FILE_NAME" />,
      accessor: (item) => item.originalFileName || '-',
      field: 'originalFileName',
      sortable: true,
    },
    {
      key: 'createdAt',
      header: <Trans i18nKey="FOGYASZTASI_HELYEK_BETOLTESE.UPLOADED_AT" />,
      accessor: (item) => formatDate(item.createdAt, DATE_TIME_FORMAT),
      field: 'createdAt',
      sortable: true,
    },
    {
      key: 'processedAt',
      header: <Trans i18nKey="FOGYASZTASI_HELYEK_BETOLTESE.PROCESSED_AT" />,
      accessor: (item) => (item.processedAt ? formatDate(item.processedAt, DATE_TIME_FORMAT) : '-'),
      field: 'processedAt',
      sortable: true,
    },
    {
      key: 'status',
      header: <Trans i18nKey="FOGYASZTASI_HELYEK_BETOLTESE.STATUS" />,
      accessor: (item) => <Trans i18nKey={STATUS_LABELS[item.status]} />,
      field: 'status',
      sortable: true,
    },
    {
      key: 'logFileName',
      header: <Trans i18nKey="FOGYASZTASI_HELYEK_BETOLTESE.LOG_FILE" />,
      accessor: (item) =>
        item.logFileName ? (
          <Button
            variant="text"
            buttonSx={styles.logDownLoadButton}
            onClick={() => downloadLog(item.id)}
          >
            <Trans i18nKey="COMMON.DOWNLOAD" />
          </Button>
        ) : (
          '-'
        ),
      field: 'logFileName',
      sortable: true,
    },
  ];

  const panels = [filterConfig];
  const { activePanel, closePanel, setPanel } = usePanelState();

  const handleUploadSubmit = async () => {
    if (selectedFiles) {
      await uploadFiles(selectedFiles);
      formikRef.current?.resetForm();
      closeConfirm();
    }
  };

  return (
    <Page
      title={<Trans i18nKey="FOGYASZTASI_HELYEK_BETOLTESE.TITLE" />}
      inScroll
      panels={panels}
      activePanel={activePanel}
      closePanel={closePanel}
      setPanel={setPanel}
    >
      <Box mb={2}>
        <Formik
          innerRef={formikRef}
          initialValues={{ file: null as File[] | null }}
          onSubmit={(values) => {
            if (values.file?.length) {
              openConfirm(values.file);
            }
          }}
        >
          {({ values, resetForm }) => (
            <Form>
              <Field
                component={FileUpload}
                multiple
                onDropRejected={resetForm}
                name="file"
                accept={{
                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                }}
              />

              <Button type="submit" disabled={!values.file?.length}>
                <Trans i18nKey="FOGYASZTASI_HELYEK_BETOLTESE.UPLOAD_BUTTON" />
              </Button>
            </Form>
          )}
        </Formik>
      </Box>

      <ConfirmDialog
        open={isConfirmOpen}
        onClose={closeConfirm}
        onSubmit={handleUploadSubmit}
        loading={isUploading}
        title={
          <Typography variant="h6">
            <Trans i18nKey="FOGYASZTASI_HELYEK_BETOLTESE.CONFIRM_TITLE" />
          </Typography>
        }
        confirmTitle={<Trans i18nKey="COMMON.YES" />}
        cancelTitle={<Trans i18nKey="COMMON.NO" />}
      >
        <Typography>
          <Trans i18nKey="FOGYASZTASI_HELYEK_BETOLTESE.CONFIRM_MESSAGE" />
        </Typography>
      </ConfirmDialog>

      <Table
        setPanel={setPanel}
        activePanel={activePanel}
        list={listData?.data || []}
        total={listData?.meta?.total}
        timestamp={dataUpdatedAt}
        loading={isLoading}
        params={params}
        onSort={onSort}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        enableCheckbox={false}
        columns={columns}
        onTableColumnVisibilityChange={onTableColumnVisibilityChange}
        onLoadQuery={onLoadQuery}
        panels={panels}
      />
    </Page>
  );
};

export default ConsumptionPlaceUploadPage;
